<template>
  <div :class="containerClasses" @mouseover="hover = true" @mouseleave="hover = false">
    <v-card flat class="ma-3" @click="redirectionToAnalysis">
      <img
        v-if="!imageLoadError"
        @error="onImageLoadError"
        :class="cardImageClasses()"
        :src="documentLeadImageUrl"
      >
      <div v-else class="card-img" />
      <div class="text-title px-4 mt-4 text-ellipsis--2" style="word-break: break-word">
        {{ documentDetails.title }}
      </div>
      <div class="text-excerpt px-4 text-ellipsis--3">
        {{ truncatedExcerpt }}
      </div>
      <div class="mt-1 d-flex align-center justify-space-between">
        <TagsListHorizontal :tags="tags" class="pl-2" />
        <v-card-actions v-if="showCardActions">
          <v-icon v-if="hasDocumentScore" x-small class="pr-1">
            fa-gem
          </v-icon>
          <v-icon v-else x-small class="pr-1" style="color: #ddd">
            fa-gem
          </v-icon>
          <span class="pr-2">
            {{ documentScore }}
          </span>
          <v-icon x-small class="pr-1">
            fa-comments
          </v-icon>
          <span class="pr-1">
            {{ totalUserActions }}
          </span>
          <v-spacer />
          <v-tooltip
            top
            v-if="deleteButton"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-hover v-slot="{ hover }">
                <v-btn
                  icon :style="hover ? 'color: red;' : ''"
                  v-on="on"
                  v-bind="attrs"
                  @click.stop="deleteClicked()"
                >
                  <v-icon small>
                    delete
                  </v-icon>
                </v-btn>
              </v-hover>
            </template>
            <span>{{ $t('components.cards.vertical.delete') }}</span>
          </v-tooltip>
        </v-card-actions>
      </div>
    </v-card>
  </div>
</template>

<script>
import TagsListHorizontal from '@/components/tags/TagsListHorizontal';
import shouldImageBeContained from '../../../tools/shouldImageBeContained';

export default {
  name: 'VerticalCard',
  components: { TagsListHorizontal },
  data() {
    return {
      savedToPortfolio: true,
      openConfirmationDialog: false,
      documentDetails: this.documentData.documentDetails,
      tags: this.documentData.tags,
      hover: false,
      imageLoadError: false,
    };
  },
  props: {
    onClick: {
      type: Boolean,
      default: false,
    },
    documentData: {
      type: Object,
      required: true,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    allowAddingToReflection: {
      type: Boolean,
      required: true,
    },
    deleteButton: {
      type: Boolean,
      default: false,
    },
    nbCards: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    containerClasses() {
      if (this.fullWidth) {
        return 'col-12';
      }

      switch (this.nbCards) {
        case 0:
          return 'col-sm-6 col-md-4 col-lg-3 col-12';
        case 1:
          return 'col-12';
        case 2:
          return 'col-6';
        case 3:
          return 'col-4';
        default:
          return 'col-3';
      }
    },
    totalUserActions() {
      return this.documentDetails.activityCount || 0;
    },
    hasDocumentScore() {
      return Number.isFinite(this.documentDetails.documentScore);
    },
    documentScore() {
      const { documentScore } = this.documentDetails;
      if (!this.hasDocumentScore) {
        return ' - ';
      }

      return (documentScore / 20).toFixed(1);
    },
    documentLeadImageUrl() {
      return this.documentDetails && this.documentDetails.leadImageUrl;
    },
    truncatedExcerpt() {
      if (this.documentData.documentDetails.excerpt) {
        return this.documentData.documentDetails.excerpt;
      }
      if (this.documentData.documentDetails.description) {
        return this.documentData.documentDetails.description;
      }

      return '';
    },
    analysisLink() {
      if (this.documentData.documentDetails.documentType === 'document') {
        return `/doc/${this.documentData.documentId}`;
      }
      return `/doc/${this.documentData.documentId}/${this.documentData.id}`;
    },
    showCardActions() {
      return this.documentData.documentDetails.documentType !== 'document';
    },
  },
  methods: {
    deleteClicked() {
      this.$emit('deleteClicked', this.documentData.id);
    },
    redirectUrl() {
      const { isUpload } = this.documentData.documentDetails;
      const { documentId } = this.documentData;
      const analysisId = this.documentData.id;

      if (isUpload) return `doc/${documentId}/${analysisId}`;

      const { url } = this.documentData.documentDetails;
      const getUrl = window.location;
      const baseUrl = `${getUrl.protocol}//${getUrl.host}/redirect`;
      const targetUrl = new URL(baseUrl);
      targetUrl.searchParams.append('redirectUrl', url);
      targetUrl.searchParams.append('analysisId', analysisId);
      return targetUrl;
    },
    redirectionToAnalysis() {
      if (this.onClick) {
        this.$emit('clicked');
      }
      window.open(this.redirectUrl(), '_blank');
    },
    cardImageClasses() {
      if (shouldImageBeContained(this.documentLeadImageUrl)) {
        return 'card-img card-img-contain';
      }

      return 'card-img card-img-cover';
    },
    onImageLoadError() {
      this.imageLoadError = true;
    },
  },
};
</script>
<style scoped>
.text-ellipsis--2{
  text-overflow:ellipsis;
  overflow:hidden;
  display: -webkit-box !important;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
}
.text-ellipsis--3{
  text-overflow:ellipsis;
  overflow:hidden;
  display: -webkit-box !important;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  white-space: normal;
}
.text-excerpt {
  font-family: Roboto,sans-serif;
  font-size: 13px;
  line-height: 18px;
  height: 50px;
  color: rgba(0, 0, 0, 0.6);
}
.text-title {
  font-family: Roboto,sans-serif;
  font-size: 15px;
  font-weight: 500;
  height: 42px;
  margin-bottom: 18px;
  color: black;
}
.card-img {
  width: 100%;
  height: 200px;
}
.card-img-cover {
  object-fit: cover;
}
.card-img-contain {
  object-fit: contain;
}

</style>
